import { API_BASE_URL, endpoints } from '../config/api';
import { getSupabaseClient } from '../supabaseClient';

/**
 * V2 Integration Service - Works with the new backend API structure
 * and the integration_definitions / integration_connections tables.
 */
export const integrationsServiceV2 = {
  /**
   * Fetches all available integration definitions from the database.
   * @returns {Promise<Array>} Array of integration definition objects.
   */
  async getIntegrationDefinitions() {
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('integration_definitions')
        .select('*')
        .eq('public_view', true)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Failed to fetch integration definitions:', error);
      throw new Error('Failed to load available integrations.');
    }
  },

  /**
   * Fetches a user's integration connections from the database.
   * @param {string} accountId - The user's account ID.
   * @returns {Promise<Array>} Array of connection objects.
   */
  async getIntegrationConnections(accountId) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('integration_connections')
      .select('*')
      .eq('account_id', accountId);
    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Fetches capabilities for a specific integration definition.
   * @param {string} integrationDefinitionId - The ID of the integration definition.
   * @returns {Promise<Array>} Array of capability objects.
   */
  async getIntegrationCapabilities(integrationDefinitionId) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('integration_capabilities')
      .select(`
        *,
        integration_tools (
          *,
          integration_tool_parameters (*)
        )
      `)
      .eq('integration_definition_id', integrationDefinitionId)
      .order('name', { ascending: true });
    if (error) throw new Error(error.message);
    return data;
  },

  /**
   * Connects an integration by sending credentials to the backend API.
   * This is the single, secure endpoint for creating and activating connections.
   * @param {string} integrationId - The ID of the integration definition.
   * @param {string} accountId - The user's account ID.
   * @param {Object} credentials - The user's credentials for the integration.
   * @returns {Promise<Object>} The API response from the backend.
   */
  async connectIntegration(integrationId, accountId, credentials) {
    const response = await fetch(`${API_BASE_URL}${endpoints.integrationsConnections}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        // TODO: Add Authorization headers if required by the API
      },
      body: JSON.stringify({
        integration_id: integrationId,
        account_id: accountId,
        credentials
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `API Error: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Executes a specific tool for a given connection with user-provided parameters.
   * This is the new, flexible testing mechanism that replaces the generic test.
   * @param {string} connectionId - The UUID of the connection.
   * @param {string} tool_id - The id of the tool to execute.
   * @param {Object} parameters - The user-provided parameters for the tool.
   * @returns {Promise<Object>} The API response from the backend.
   */
  async executeTool(connectionId, tool_id, parameters) {
    const response = await fetch(`${API_BASE_URL}${endpoints.integrationsExecuteTool(connectionId)}`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        // TODO: Add Authorization headers if required by the API
      },
      body: JSON.stringify({ tool_id, parameters })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `API Error: ${response.status}`);
    }

    return await response.json();
  },

  /**
   * Updates the status of a connection in the database.
   * @param {string} connectionId - The UUID of the connection.
   * @param {string} status - The new status ('active', 'error', 'disabled').
   * @param {Object} additionalData - Any other fields to update.
   * @returns {Promise<Object>} The updated connection object.
   */
  async updateConnectionStatus(connectionId, status, additionalData = {}) {
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('integration_connections')
        .update({
          status,
          updated_at: new Date().toISOString(),
          ...additionalData
        })
        .eq('id', connectionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Failed to update connection status:', error);
      throw new Error('Failed to update the integration connection.');
    }
  },

  /**
   * Deletes an integration connection from the database.
   * @param {string} connectionId - The UUID of the connection to delete.
   * @returns {Promise<void>}
   */
  async deleteConnection(connectionId) {
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase
        .from('integration_connections')
        .delete()
        .eq('id', connectionId);

      if (error) throw error;
    } catch (error) {
      console.error('Failed to delete connection:', error);
      throw new Error('Failed to delete the integration.');
    }
  }
};

/**
 * Helper function to identify V2 integrations
 * @param {string} integrationId - Integration ID to check
 * @returns {boolean} True if it's a V2 integration
 */
export const isV2Integration = (integrationId) => {
  const v2IntegrationIds = ['cleancloud', 'cci_fascard', 'cci_laundrycard', 'ownerrez_v2'];
  return v2IntegrationIds.includes(integrationId);
}; 